import { Form, Input, message } from "antd";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { MakeApiCall } from "../../../apis";

/* ── Animations ── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: #263d57;
  overflow: hidden;

  /* ── ANT overrides ── */
  .ant-input-affix-wrapper,
  .ant-input {
    background: #fafafa !important;
    border: 1.5px solid #e8e8ea !important;
    border-radius: 10px !important;
    font-size: 13.5px !important;
    height: 46px !important;
    padding: 0 14px !important;
    color: #1d1e20 !important;
    box-shadow: none !important;
    transition: border-color 0.15s !important;
  }
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input:focus {
    border-color: #f35b68 !important;
    background: #fff !important;
  }
  .ant-input::placeholder { color: #ccc !important; }
  .ant-input-affix-wrapper .ant-input {
    height: 100% !important;
    border: none !important;
    background: transparent !important;
  }
  .ant-form-item { margin-bottom: 12px !important; }
  .ant-form-item-explain-error {
    font-size: 12px !important;
    margin-top: 4px !important;
    color: #f07070 !important;
  }
`;

/* ── LEFT panel ── */
const Left = styled.div`
  width: 520px;
  min-width: 420px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px 48px;
  box-shadow: 12px 0 48px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 900px) {
    width: 100%;
    min-width: unset;
    box-shadow: none;
    padding: 44px 32px;
  }
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: #1d1e20;
  margin-bottom: 40px;
  img { height: 20px; object-fit: contain; }
`;

const Heading = styled.h1`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #1d1e20;
  line-height: 1.25;
  margin: 0 0 6px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #535353;
  font-weight: 300;
  margin: 0 0 28px;
`;

const Label = styled.label`
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 5px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 13px;
  background: #f35b68;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  transition: background 0.15s, transform 0.1s;
  letter-spacing: -0.1px;

  &:hover  { background: #d94757; }
  &:active { transform: scale(0.99); }
  &:disabled { opacity: 0.7; cursor: not-allowed; }

  .icon {
    width: 22px; height: 22px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
  }
`;

const BackLink = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: #535353;
  margin-top: 18px;
  a { color: #f35b68; text-decoration: none; font-weight: 500; }
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #f0f0ee;
  font-size: 11px;
  color: #535353;
  font-weight: 300;
  a { color: #f35b68; text-decoration: none; font-weight: 500; }
`;

/* ── RIGHT panel ── */
const Right = styled.div`
  flex: 1;
  background: #355071;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 64px;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) { display: none; }
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  opacity: 0.18;
  &.g1 { width: 360px; height: 360px; background: #f35b68; top: -80px; right: -80px; }
  &.g2 { width: 260px; height: 260px; background: #fff; bottom: -60px; left: -60px; opacity: 0.04; }
`;

const Ring = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 0.5px solid rgba(255, 255, 255, 0.06);
  &.r1 { width: 640px; height: 640px; top: -260px; right: -220px; }
  &.r2 { width: 420px; height: 420px; top: -140px; right: -100px; border-color: rgba(243, 91, 104, 0.12); }
  &.r3 { width: 520px; height: 520px; bottom: -320px; left: -220px; }
`;

const RightInner = styled.div`
  max-width: 460px;
  width: 100%;
  position: relative;
  z-index: 1;
  text-align: center;
  animation: ${fadeUp} 0.7s ease 0.2s both;
`;

const BigLock = styled.div`
  width: 120px;
  height: 120px;
  margin: 0 auto 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 0.5px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: rgba(255, 255, 255, 0.85);

  svg { width: 46px; height: 46px; }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  color: #f9868f;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  margin-bottom: 16px;
  span { width: 20px; height: 1.5px; background: #f35b68; opacity: 0.5; border-radius: 1px; display: inline-block; }
`;

const StoryHeadline = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.6px;
  line-height: 1.25;
  margin: 0 0 16px;
  em { color: #f35b68; font-style: normal; }
`;

const StoryBody = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 300;
  line-height: 1.7;
  max-width: 380px;
  margin: 0 auto 28px;
  strong { color: rgba(255, 255, 255, 0.85); font-weight: 500; }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 0.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  padding: 10px 18px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 300;
  strong { color: #f9868f; font-weight: 500; }
`;

export default function ForgotPassword() {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(values.email)) {
      message.error("Please enter a valid email address");
      return;
    }

    setSubmitLoading(true);
    try {
      const response = await MakeApiCall("forgot-password", "post", {
        email: values.email,
      });

      if (response?.status) {
        message.destroy();
        message.success(`Reset link sent to ${values.email}`);
      } else {
        message.destroy();
        const fieldErrors = response?.data;
        const firstFieldError =
          fieldErrors && typeof fieldErrors === "object"
            ? Object.values(fieldErrors).flat().find(Boolean)
            : null;
        message.error(firstFieldError || response?.message || "Failed to send reset link");
      }
    } catch (err) {
      message.destroy();
      message.error("Something went wrong.");
    }
    setSubmitLoading(false);
  };

  return (
    <Wrapper className="h-100">
      {/* ── LEFT: FORM ── */}
      <Left>
        <Logo>
          <img src="assets/media/SellerViewLogo.png" alt="Sellerview" />
        </Logo>

        <Heading>
          Forgot your password?
          <br />
          Happens to the best of us.
        </Heading>
        <SubText>
          Pop in your email and we'll send you a reset link. Your numbers will be right where you left them.
        </SubText>

        <Form form={form} name="forgot-password" onFinish={onFinish}>
          <Label>Work Email</Label>
          <Form.Item
            name="email"
            rules={[{ required: true, message: "Please input your email!" }]}
          >
            <Input placeholder="you@yourstore.com" size="large" />
          </Form.Item>

          <Form.Item style={{ marginTop: 6 }}>
            <SubmitBtn type="submit" disabled={submitLoading}>
              {submitLoading ? (
                "Please wait…"
              ) : (
                <>
                  Send Reset Link <div className="icon">→</div>
                </>
              )}
            </SubmitBtn>
          </Form.Item>
        </Form>

        <BackLink>
          <Link to="/sign-in">← Back to Sign in</Link>
        </BackLink>

        <TrustRow>
          Still locked out? We've got you — reach{" "}
          <a href="mailto:support@sellerview.ai">support@sellerview.ai</a> and a human (not a bot) will sort it.
        </TrustRow>
      </Left>

      {/* ── RIGHT: STORY PANEL ── */}
      <Right>
        <Ring className="r1" />
        <Ring className="r2" />
        <Ring className="r3" />
        <Glow className="g1" />
        <Glow className="g2" />

        <RightInner>
          <BigLock>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 9.9-1" />
            </svg>
          </BigLock>

          <Eyebrow>
            <span />
            Don't worry
            <span />
          </Eyebrow>

          <StoryHeadline>
            Your P&amp;L isn't going anywhere. <em>Promise.</em>
          </StoryHeadline>

          <StoryBody>
            Every SKU, every margin, every "wait, why is this one losing money?" insight —{" "}
            <strong>all safe.</strong> One click and you're back in business.
          </StoryBody>

          <Badge>
            Passwords expire. <strong>Good unit economics don't.</strong>
          </Badge>
        </RightInner>
      </Right>
    </Wrapper>
  );
}
